import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import SlotsPage from './pages/SlotsPageNew';
import DevicesPage from './pages/DevicesPageNew';
import SessionsPage from './pages/SessionsPage';
import SmmOrdersPage from './pages/SmmOrdersPage';
import SmmSettingsPage from './pages/SmmSettingsPage';
import LogsPage from './pages/LogsPage';
import SettingsPage from './pages/SettingsPage';
import CampaignsPage from './pages/CampaignsPage';
import RepostingPage from './pages/RepostingPage';
import SlotSettingsPage from './pages/SlotSettingsPage';
import SlotTargetDataPage from './pages/SlotTargetDataPage';
import SlotStatsPage from './pages/SlotStatsPage';
import DeviceSettingsPage from './pages/DeviceSettingsPage';
import PhoneMonitorPage from './pages/PhoneMonitorPage';
import VersionHistoryPage from './pages/VersionHistoryPage';
import InventoryPage from './pages/InventoryPage';
import FollowBackPage from './pages/FollowBackPage';
import SmmSubscriptionsPage from './pages/SmmSubscriptionsPage';
import InbuiltScrapingPage from './pages/InbuiltScrapingPage';
import { r16o3UiEnabled } from './lib/r14Switch';
import InboxPage from './pages/InboxPage';
import ApiSettingsPage from './pages/ApiSettingsPage';
import ChatDataPage from './pages/ChatDataPage';
// Admin Pages
import FeatureFlagsPage from './pages/admin/FeatureFlagsPage';
import SupportTicketsPage from './pages/admin/SupportTicketsPage';
import AssistantPage from './pages/AssistantPage';
import TenantsPage from './pages/admin/TenantsPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import UIDebugViewerPage from './pages/admin/UIDebugViewerPage';
import BrandingSettingsPage from './pages/reseller/BrandingSettingsPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import Layout from './components/Layout';
import ChatsCacheGuard from './components/ChatsCacheGuard';
import DoctorCacheGuard from './components/doctor/DoctorCacheGuard';

import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * An entitlement gate on a ROUTE, not just on a menu item.
 *
 * <p>Hiding a nav link is presentation; it is not a gate, because the URL still works and this app
 * is one bookmark away from being shared. V1 gated by not rendering the link AND by hiding the
 * whole block on the page itself (boosting-order.html lines 124-350), so a V1 user who typed the
 * URL got an empty page. This is the same rule expressed once.
 *
 * <p>Admins pass regardless - see the note in Layout's NavGroups. It grants nothing: the server
 * checks the entitlement on every SMM path of its own accord.
 *
 * <p>While `isLoading` is true `user` is null, so we must not redirect yet or a refresh on
 * /smm-settings would bounce an entitled customer to the dashboard. ProtectedRoute already holds
 * the spinner during that window; this only ever renders after it.
 */
function EntitledRoute({ requires, children }) {
  const { user, isLoading } = useAuth();
  if (isLoading || !user) return null;
  const allowed = user.role === 'owner' || user.isAdmin || !!user.flags?.[requires];
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

/** r16o3 v3-backlog: the two pages rebuilt from the legacy UI exist only while lib/r14Switch r16o3UiEnabled is ON (OFF -> the dashboard). */
function R16o3Route({ children }) {
  return r16o3UiEnabled() ? children : <Navigate to="/dashboard" replace />;
}

/* r16p: /slots/:slotName/inbox is an alias of /inbox?slot= (the Slots page's Inbox link). Slot NAMES only, never a handle. */
function SlotInboxRedirect() {
  const { slotName } = useParams();
  const slot = typeof slotName === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(slotName) ? slotName : null;
  return <Navigate to={slot ? `/inbox?slot=${encodeURIComponent(slot)}` : '/inbox'} replace />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <AuthProvider>
          {/* r16p review T1: drops the chat and API-key caches (and reply drafts) when the signed-in account changes */}
          <ChatsCacheGuard />
          {/* r16sd review MF2: drops the Settings Doctor caches (credits, banners, slot panels) when the signed-in account changes */}
          <DoctorCacheGuard />
          <BrowserRouter>
            <div className="dark min-h-screen bg-background text-foreground">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<DashboardPage />} />
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/monitor" element={<PhoneMonitorPage />} />
                          <Route path="/versions" element={<VersionHistoryPage />} />
                          <Route path="/slots" element={<SlotsPage />} />
                          <Route path="/slots/:slotName/settings" element={<SlotSettingsPage />} />
                          <Route path="/slots/:slotId/targets" element={<SlotTargetDataPage />} />
                          <Route path="/slots/:slotId/stats" element={<SlotStatsPage />} />
                          {/* r16m: follow-back by source type and target, per batch or slot range (lib/r14Switch r16mUiEnabled; OFF -> dashboard) */}
                          <Route path="/follow-back" element={<FollowBackPage />} />
                          {/* r16p: the DM inbox (lib/r14Switch r16pUiEnabled; OFF -> dashboard). ?slot=&thread=<conv id>&filter= */}
                          <Route path="/inbox" element={<InboxPage />} />
                          <Route path="/slots/:slotName/inbox" element={<SlotInboxRedirect />} />
                          <Route path="/devices" element={<DevicesPage />} />
                          <Route path="/devices/:deviceId/settings" element={<DeviceSettingsPage />} />
                          {/* r16 Inventory: every row is scoped by the server; a client sees only its own part. */}
                          <Route path="/inventory" element={<Navigate to="/inventory/accounts" replace />} />
                          <Route path="/inventory/:tab" element={<InventoryPage />} />
                          <Route path="/sessions" element={<SessionsPage />} />
                          <Route path="/smm-orders" element={<EntitledRoute requires="enableSmmFeature"><SmmOrdersPage /></EntitledRoute>} />
                          <Route path="/smm-settings" element={<EntitledRoute requires="enableSmmFeature"><SmmSettingsPage /></EntitledRoute>} />
                          {/* r16o3: V1 /smmSubscriptionOrders/ (pause / resume) and the inbuilt-scraping block of V1 /orderManagement/accountSettings */}
                          <Route path="/smm-subscriptions" element={<R16o3Route><EntitledRoute requires="enableSmmFeature"><SmmSubscriptionsPage /></EntitledRoute></R16o3Route>} />
                          <Route path="/inbuilt-scraping" element={<R16o3Route><InbuiltScrapingPage /></R16o3Route>} />
                          <Route path="/logs" element={<LogsPage />} />
                          <Route path="/campaigns" element={<CampaignsPage />} />
                          <Route path="/reposting" element={<RepostingPage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/settings/system" element={<SystemSettingsPage />} />
                          {/* r16p: API keys, Lock API access, Docs (tabs in the URL, like /inventory/:tab) */}
                          <Route path="/settings/api" element={<Navigate to="/settings/api/keys" replace />} />
                          <Route path="/settings/api/:tab" element={<ApiSettingsPage />} />
                          {/* r16pm: Chat data - the account's retention (O8) and the slot-data erase (lib/r14Switch r16pUiEnabled; OFF -> dashboard) */}
                          <Route path="/settings/chats" element={<ChatDataPage />} />
                          {/* Admin Routes */}
                          <Route path="/admin/feature-flags" element={<FeatureFlagsPage />} />
                          <Route path="/admin/support" element={<SupportTicketsPage />} />
                          <Route path="/assistant" element={<AssistantPage />} />
                          <Route path="/admin/tenants" element={<TenantsPage />} />
                          <Route path="/admin/users" element={<UserManagementPage />} />
                          <Route path="/admin/ui-debug" element={<UIDebugViewerPage />} />
                          {/* Reseller Routes */}
                          <Route path="/reseller/branding" element={<BrandingSettingsPage />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
              <Toaster richColors position="top-right" />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
}

export default App;
